import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of, catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { printersActions } from '../actions/printers.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class PrintersEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);

    getPrinters$ = createEffect(() => {
        return this.actions.pipe(
            ofType(printersActions.getPrinters),
            switchMap(({ withStatus, background }) => {
                const request = this.httpService.getPrinters();
                return (background ? request : request.pipe(finalizeLoader('printers-list'))).pipe(
                    map((payload) => printersActions.getPrintersSuccess({ payload, withStatus })),
                    catchError((response: HttpErrorResponse) => of(printersActions.getPrintersError({ code: response.status }))),
                );
            }),
        );
    });

    // No loader: this only feeds the station form's handled-labels preview, which is supplementary.
    getLabelDimensions$ = createEffect(() => {
        return this.actions.pipe(
            ofType(printersActions.getLabelDimensions),
            switchMap(() =>
                this.httpService.getLabelDimensions().pipe(
                    map((payload) => printersActions.getLabelDimensionsSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => of(printersActions.getLabelDimensionsError({ code: response.status }))),
                ),
            ),
        );
    });

    // Live status per row, max 3 concurrent; cancelled on list reload and on page leave.
    loadStatuses$ = createEffect(() => {
        return this.actions.pipe(
            ofType(printersActions.getPrintersSuccess),
            filter(({ withStatus }) => withStatus),
            switchMap(({ payload }) =>
                from(payload).pipe(
                    mergeMap(
                        (printer) =>
                            this.httpService.getPrinterStatus(printer.id).pipe(
                                map((status) => printersActions.printerStatusLoaded({ id: printer.id, status })),
                                catchError(() => of(printersActions.printerStatusError({ id: printer.id }))),
                            ),
                        3,
                    ),
                    takeUntil(this.actions.pipe(ofType(printersActions.clear))),
                ),
            ),
        );
    });
}
